import { isEqual } from 'underscore';
import { Line, Logger, Point } from '../../../../../FrontendBundle/Resources/js/ofcs';
import CutangleItem from '../item/cutangleItem';
import CutsquareItem from '../item/cutsquareItem';
import DrillItem from '../item/drillItem';
import VentilationgrillItem from '../item/ventilationgrillItem';
import IntersectionCollision from './collision/intersectionCollision';

/**
 * @deprecated This class should be refactored at some point. It does not fit with the other collision classes and does
 * far to much! It should not have any side effects (like setting the item area etc.)
 * @extends ItemCollisionChecker
 */
export default class TabletopCollisionChecker {
    /**
     * @param {AbstractItem} item
     * @return {IntersectionCollision[]}
     * @private
     */
    check(item) {
        if (item instanceof CutangleItem) return this._checkCutangleItem(item);
        return this._checkItemAgainstCuts(item, this._getCuts(item));
    }

    /**
     * @param {AbstractItem} item
     * @param {CutangleItem[]} cuts
     * @return {IntersectionCollision[]}
     * @private
     */
    _checkItemAgainstCuts(item, cuts) {
        if (item instanceof CutsquareItem) {
            return this._checkCutsquareItem(item, cuts);
        }

        if (item instanceof DrillItem) {
            return this._checkDrillItem(item, cuts);
        }

        if (item instanceof VentilationgrillItem) {
            return this._checkVentilationGrillItem(item, cuts);
        }

        return [];
    }

    /**
     * @param {CutsquareItem} item
     * @param {CutangleItem[]} cuts
     * @return {IntersectionCollision[]}
     * @private
     */
    _checkCutsquareItem(item, cuts) {
        const { cut, collision, collisionCuts } = this._checkCutsquareCuts(item, cuts);

        if (!cut) {
            return this._checkCutsquareCollisions(item, cuts);
        }

        if (collision) {
            return collisionCuts.map(collisionCut => new IntersectionCollision(item, collisionCut));
        }

        return [];
    }

    /**
     * @param {DrillItem} item
     * @param {CutangleItem[]} cuts
     * @return {IntersectionCollision[]}
     * @private
     */
    _checkDrillItem(item, cuts) {
        const { cut, collision, collisionCuts } = this._checkDrillCuts(item, cuts);

        if (!cut && !collision) {
            return this._checkDrillCollisions(item, cuts);
        }

        if (collision) {
            return collisionCuts.map(collisionCut => new IntersectionCollision(item, collisionCut));
        }

        return [];
    }

    /**
     * @param {VentilationgrillItem} item
     * @param {CutangleItem[]} cuts
     * @return {IntersectionCollision[]}
     * @private
     */
    _checkVentilationGrillItem(item, cuts) {
        const { collision, collisionCuts } = this._checkVentilationCuts(item, cuts);
        if (collision) {
            return collisionCuts.map(collisionCut => new IntersectionCollision(item, collisionCut));
        }

        return [];
    }

    _checkCutangleItem(item) {
        const coll = item
            .getParentItem()
            .getItems()
            .filter(other => !(other instanceof CutangleItem))
            .map(other => this._checkItemAgainstCuts(other, [item]))
            .reduce((flattened, collisions) => flattened.concat(collisions), []);

        return coll;
    }

    /**
     * Checks if any registered cut lines intersect with the given cutsquare item and calculate its area accordingly
     *
     * @param {CutsquareItem} item - cutsquare item
     * @param {CutangleItem[]} cuts
     * @return {boolean}
     */
    _checkCutsquareCuts(item, cuts) {
        const {
            collision,
            collisionCuts,
            intersections,
            path,
        } = this._checkCutsquareIntersect(item, cuts, false);

        if (collision) {
            const cutoff = this._getCutsquareCutoff(intersections, path, cuts, item);
            this._calculateCutsquareArea(cutoff, item);
            item.set('billable', true, { silent: true });
            return { cut: true, collision: cutoff.collision, collisionCuts };
        }

        const area = item.get('width') * item.get('height');
        item.set('area', area, { silent: true });

        return { cut: false };
    }

    /**
     * Checks if any registered cut lines intersect with the given cutsquare item and calculates its intersection
     * points accordingly
     *
     * @param {CutsquareItem} item - cutsquare item
     * @param {CutangleItem[]} cuts
     * @param {boolean} withMargins
     * @return {{collision: boolean, intersections: Array, path: Array}}
     */
    _checkCutsquareIntersect(item, cuts, withMargins) {
        const tl = item.getPointTopLeft(withMargins);
        const tr = item.getPointTopRight(withMargins);
        const bl = item.getPointBottomLeft(withMargins);
        const br = item.getPointBottomRight(withMargins);
        const segments = [
            new Line(tl, tr),
            new Line(tr, br),
            new Line(br, bl),
            new Line(bl, tl),
        ];
        const path = [tl, tr, br, bl];
        const collisionCuts = [];
        let collision = false;

        const intersections = cuts
            .map(cut => segments.map((segment) => {
                let intersection = segment.projectIntersection(cut.get('line'));
                if (intersection && intersection.first === true && intersection.second === true) {
                    intersection = new Point(intersection.x, intersection.y);
                    collisionCuts.push(cut);
                } else {
                    intersection = false;
                }

                collision = intersection ? true : collision;
                return intersection;
            }));

        return {
            collision,
            collisionCuts,
            intersections,
            path,
        };
    }

    /**
     * Extracts cutoff polygon from intersections points in a cutsquare item
     *
     * @param {Point[]} intersections
     * @param {Point[]} path
     * @param {CutangleItem[]} cuts
     * @return {{poly: Array, inside: Array, collision: Array}}
     */
    _getCutsquareCutoff(intersections, path, cuts) {
        const inside = [];
        const poly = [];
        let collision = false;
        const paused = {};
        const count = {};

        for (let i = 0; i < intersections.length; i += 1) {
            paused.status = false;
            paused.index = 0;
            count.out = 0;
            count.in = 0;
            poly.push([]);
            for (let j = 0; j < intersections[i].length; j += 1) {
                if (paused.status === true && !inside[i]) {
                    inside[i] = !this._pointInPoly(path[j], cuts[i].getOutlinePath());
                }
                if (paused.status === false) {
                    poly[i].push(path[j]);
                }
                if (intersections[i][j]) {
                    // if intersections and path vertices collide
                    // they will be detected at the end of the respective line
                    poly[i].push(intersections[i][j]);
                    if (paused.status === false && isEqual(intersections[i][j], path[j + 1])) {
                        count.in += 1;
                    }
                    paused.status = paused.status === false;
                    paused.index = j;
                }
                if (j === intersections[i].length - 1) {
                    if (!isEqual(intersections[i][j], path[0])) {
                        poly[i].push(path[0]);
                    } else {
                        count.out += 1;
                    }
                }

                count.out = paused.status === true && j !== paused.index ? count.out + 1 : count.out;
                count.in = paused.status === false && (paused.index === 0 || (paused.index > 0 && j !== paused.index))
                    ? count.in + 1
                    : count.in;
            }

            collision = inside[i] === undefined || ((count.out > 0 && inside[i] === false)
                || (count.in > 0 && inside[i] === true)) ? collision : true;
            Logger.debug('cutsquare collisions', collision, count.out, count.in, inside[i]);
        }

        return { poly, inside, collision };
    }

    /**
     * Calculates cutsquare area from the given information
     * and updates the item's area property accordingly
     *
     * @param {{poly: Array, inside: Array}} data - hands in polygon and intersection information
     * @param {CutsquareItem} item - cutsquare item
     */
    _calculateCutsquareArea({ poly, inside }, item) {
        const basis = item.get('width') * item.get('height');
        let area = 0;

        for (let i = 0; i < poly.length; i += 1) {
            if (inside[i]) {
                area += this._getPolyArea(poly[i]);
            } else {
                area += basis - this._getPolyArea(poly[i]);
            }
        }

        item.set('area', basis - area, { silent: true });
    }

    /**
     * Checks for invalid margin collisions
     * @param {CutsquareItem} item - cutsquare item
     * @param {CutangleItem[]} cuts
     * @return {IntersectionCollision[]}
     */
    _checkCutsquareCollisions(item, cuts) {
        if (this._pointsInsideTable(item, [
            item.getPointTopLeft(),
            item.getPointTopRight(),
            item.getPointBottomLeft(),
            item.getPointBottomRight(),
        ])) {
            const temp = this._checkCutsquareIntersect(item, cuts, true);
            item.set('billable', true, { silent: true });
            if (temp.collision) {
                return temp.collisionCuts.map(collisionCut => new IntersectionCollision(item, collisionCut));
            }
            return [];
        }

        item.set('billable', false, { silent: true });
        return [];
    }

    /**
     *
     * Checks if any registered cut lines intersect with
     * the given drill item and calculates its intersection
     * points accordingly
     *
     * @param {CutangleItem[]} cuts
     * @param {DrillItem} item - drill item
     * @param {boolean} withMargin
     * @return {{collision: boolean, intersections: Array, inside: Array}}
     */
    _checkDrillIntersect(cuts, item, withMargin) {
        const r = item.getRadius(withMargin);
        const c = item.get('position');
        let collision = false;
        // let j = 0;
        const intersections = [];
        const inside = [];
        const collisionCuts = [];

        cuts.forEach((cut, idx) => {
            const intersection = this._circleLineIntersect(r, c, cut.get('line'));
            intersections[idx] = intersection.length > 1 ? intersection : [];
            inside[idx] = intersections[idx] ? this._pointInPoly(c, cut.getOutlinePath()) : undefined;

            if (intersection.length > 1) {
                collisionCuts.push(cut);
                collision = true;
            }
        });

        return {
            collision,
            intersections,
            inside,
            collisionCuts,
        };
    }

    /**
     * Calculates drill area from the given information
     * and updates the item's area property accordingly
     *
     * @param {Array} intersections
     * @param {Array} inside
     * @param {DrillItem} item - drill item
     * @return {boolean}
     */
    _calculateDrillArea(intersections, inside, item) {
        const basis = item.getArea();
        let area = 0;
        let collision = true;

        for (let i = 0; i < intersections.length; i += 1) {
            if (intersections[i].length > 0) {
                if (inside[i]) {
                    area += basis - item.getArea(intersections[i][0], intersections[i][1]);
                    collision = false;
                } else {
                    area += item.getArea(intersections[i][0], intersections[i][1]);
                }
            }
        }

        item.set('area', basis - area, { silent: true });

        // invalidate drill when there is a cut and a cover is selected, no matter if the cut itself ma be valid
        return item.get('cover') !== 'without' ? true : collision;
    }

    /**
     * Checks if any registered cut lines intersect with
     * the given drill item and calculates its area
     * accordingly
     *
     * @param {DrillItem} item - drill item
     * @param {CutangleItem[]} cuts
     * @return {boolean}
     */
    _checkDrillCuts(item, cuts) {
        const {
            collision,
            intersections,
            inside,
            collisionCuts,
        } = this._checkDrillIntersect(cuts, item, false);

        if (collision) {
            const cutOff = this._calculateDrillArea(intersections, inside, item);
            item.set('billable', true, { silent: true });
            return { cut: true, collision: cutOff, collisionCuts };
        }

        return { cut: false };
    }

    /**
     * Checks for invalid margin collisions
     * @param {DrillItem} item - drill item
     * @param {CutangleItem[]} cuts
     * @return {IntersectionCollision[]}
     */
    _checkDrillCollisions(item, cuts) {
        if (this._pointsInsideTable(item, [
            item.getPointTop(),
            item.getPointRight(),
            item.getPointBottom(),
            item.getPointLeft(),
        ])) {
            const temp = this._checkDrillIntersect(cuts, item, true);
            item.set('billable', true, { silent: true });
            if (temp.collision) {
                return temp.collisionCuts.map(collisionCut => new IntersectionCollision(item, collisionCut));
            }
            return [];
        }

        item.set('billable', false, { silent: true });
        return [];
    }

    /**
     * Calculates circle and line intersections
     *
     * @param {number} r - circle radius
     * @param {Point} centre - circle centre
     * @param {Line} line - intersecting line
     * @return {Array}
     */
    _circleLineIntersect(r, centre, line) {
        const h = centre.x;
        const k = centre.y;
        const m = (line.endPoint.y - line.startPoint.y) / (line.endPoint.x - line.startPoint.x);
        const n = line.startPoint.y - (m * line.startPoint.x);

        // get a, b, c values
        const a = 1 + (m * m);
        const b = (-h * 2) + ((m * (n - k)) * 2);
        const c = (h * h) + (((n - k) * (n - k)) - (r * r));

        // get discriminant
        const d = (b * b) - (4 * a * c);
        if (d >= 0) {
            // insert into quadratic formula
            const x1 = (-b + Math.sqrt((b * b) - (4 * a * c))) / (2 * a);
            const x2 = (-b - Math.sqrt((b * b) - (4 * a * c))) / (2 * a);
            const intersections = [
                new Point(x1, ((x1 * m) + n)),
                new Point(x2, ((x2 * m) + n)),
            ];
            if (d === 0) {
                // only 1 intersection
                return [intersections[0]];
            }
            return intersections;
        }
        // no intersection
        return [];
    }

    _checkVentilationCuts(item, cuts) {
        const withMargins = true;
        const tl = item.getPointTopLeft(withMargins);
        const tr = item.getPointTopRight(withMargins);
        const bl = item.getPointBottomLeft(withMargins);
        const br = item.getPointBottomRight(withMargins);
        const segments = [
            new Line(tl, tr),
            new Line(tr, br),
            new Line(br, bl),
            new Line(bl, tl),
        ];
        const collisionCuts = [];
        let collision = false;
        let outside = 0;
        let j = 0;

        while (cuts[j]) {
            for (let i = 0; i < segments.length; i += 1) {
                const temp = segments[i].projectIntersection(cuts[j].get('line'));
                collision = temp.first === true && temp.second === true ? true : collision;
            }
            if (collision) {
                collisionCuts.push(cuts[j]);
                const outline = cuts[j].getOutlinePath();
                const path = [
                    item.getPointTopLeft(),
                    item.getPointTopRight(),
                    item.getPointBottomLeft(),
                    item.getPointBottomRight(),
                ];
                for (let i = 0; i < path.length; i += 1) {
                    outside = this._pointInPoly(path[i], outline) ? outside + 1 : outside;
                }
                if (outside === 4) {
                    collision = false;
                    item.set('billable', false, { silent: true });
                } else {
                    collision = true;
                    item.set('billable', true, { silent: true });
                }
                j = cuts.length;
            }
            j += 1;
        }

        return { collision, collisionCuts };
    }

    /**
     * Returns all currently registered cut items
     * @param {AbstractItem} item
     * @return {CutangleItem[]}
     */
    _getCuts(item) {
        return Object.values(item.getParentItem().cutIndex).map(index => index.item);
    }

    /**
     * Checks if given points are inside
     * the currently active table area
     *
     * @param {AbstractItem} item
     * @param {Point[]} points
     * @return {boolean}
     */
    _pointsInsideTable(item, points) {
        const table = item.getParentItem();
        const outlines = table.getOutlinePath();

        return !!points.find(point => this._pointInPoly(point, outlines));
    }

    /**
     * Checks if given Point lies within given Polygon
     * When Point collides with polygon it may return true or false
     *
     * @param {Point} point - point to be checked
     * @param {Point[]} poly - array existing of polygon path points
     * @return {boolean} returns true when point lies inside
     */
    _pointInPoly(point, poly) {
        let oddNodes = false;

        // remove duplicate start/end point for calculation to work
        const polyClone = [...poly];
        polyClone.pop();

        let j = polyClone.length - 1;

        for (let i = 0; i < polyClone.length; i += 1) {
            if (((polyClone[i].y <= point.y && polyClone[j].y >= point.y)
                || (polyClone[j].y <= point.y && polyClone[i].y >= point.y))
                && (polyClone[i].x <= point.x || polyClone[j].x <= point.x)) {
                // eslint-disable-next-line no-bitwise
                oddNodes ^= (polyClone[i].x + (
                    ((point.y - polyClone[i].y) / (polyClone[j].y - polyClone[i].y))
                    * (polyClone[j].x - polyClone[i].x)
                ) < point.x);
            }
            j = i;
        }

        return Boolean(oddNodes);
    }

    /**
     * Get the area for a polygon
     *
     * @param {Point[]} path - polygon path
     * @return {number}
     */
    _getPolyArea(path) {
        return Ofcs.Editor.Svg.Handle.CutangleHelper.prototype.getPolygonArea(path);
    }
}
